import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";

/**
 * P3 Live-Modus — developer-only Schalter (Einstellungen).
 * LIVE = sibit.xp0.ai serviert die Dev-Runtime (Hot-Reload aus philippos-Checkout),
 * admin/user sehen die Wartungsseite. STABIL = gebackener, freigegebener Stand.
 * Der POST setzt nur den transition-Lock; der Box-Orchestrator führt den Wechsel
 * aus und setzt danach den Modus — die UI pollt bis der Übergang fertig ist.
 */
export function SettingsLiveMode() {
  const [mode, setMode] = useState<"stable" | "live" | null>(null);
  const [transition, setTransition] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [version, setVersion] = useState("");
  const [git, setGit] = useState<{ branch: string | null; commit: string | null }>({ branch: null, commit: null });
  const poll = useRef<number | null>(null);

  const load = async () => {
    try {
      const m = await api.system.mode();
      setMode(m.mode);
      setTransition(m.transition);
      return m;
    } catch (e: any) {
      setMsg(`Fehler: ${e.message}`);
      return null;
    }
  };

  useEffect(() => {
    load();
    api.system.version().then((v) => { setVersion(v.version); setGit({ branch: v.branch ?? null, commit: v.commit ?? null }); }).catch(() => {});
    return () => { if (poll.current) window.clearInterval(poll.current); };
  }, []);

  // Solange ein Übergang läuft: alle 3s pollen, bis transition leer ist.
  useEffect(() => {
    if (transition && poll.current === null) {
      poll.current = window.setInterval(async () => {
        const m = await load();
        if (m && !m.transition) {
          if (poll.current) { window.clearInterval(poll.current); poll.current = null; }
          setBusy(false);
          setMsg(m.mode === "live" ? "✓ Live-Modus aktiv." : "✓ Stabil-Modus aktiv.");
        }
      }, 3000);
    }
    if (!transition && poll.current !== null) {
      window.clearInterval(poll.current);
      poll.current = null;
    }
  }, [transition]);

  const toLive = async () => {
    if (!window.confirm(
      "LIVE-MODUS auf dem PRODUKTIVSYSTEM aktivieren?\n\n" +
      "• Kunde (admin/user) wird ausgesperrt (Wartungsseite); nur du als developer arbeitest weiter.\n" +
      "• Es wird AUTOMATISCH ein DB-Backup angelegt — für zusätzliche Off-Box-Sicherung bist du verantwortlich.\n" +
      "• Der Hub läuft danach als Dev-Runtime (Hot-Reload aus dem philippos-Checkout).\n\n" +
      "Fortfahren?",
    )) return;
    setBusy(true);
    setMsg("Aktiviere Live-Modus… (Backup + Umschaltung, erster Lauf kann 1–2 Min dauern)");
    try {
      const r = await api.system.setMode("live");
      setTransition(r.transition);
    } catch (e: any) {
      setBusy(false);
      setMsg(`Fehler: ${e.message}`);
    }
  };

  const toStable = async () => {
    if (!window.confirm(
      "Zurück in den STABIL-Modus (Kundenbetrieb)?\n\nDie Dev-Runtime wird heruntergefahren, der freigegebene gebackene Stand läuft wieder, die Wartung wird beendet.",
    )) return;
    setBusy(true);
    setMsg("Schalte auf Stabil…");
    try {
      const r = await api.system.setMode("stable");
      setTransition(r.transition);
    } catch (e: any) {
      setBusy(false);
      setMsg(`Fehler: ${e.message}`);
    }
  };

  return (
    <div>
      <div className="kv-card" style={{ marginTop: 14 }}>
        <div className="kv-header"><strong>Betriebsmodus — Live-Entwicklung</strong></div>
        <p style={{ fontSize: 13, color: "#555", margin: "8px 0", lineHeight: 1.5 }}>
          Nur für <strong>developer</strong>. Im <strong>Live-Modus</strong> serviert sibit.xp0.ai die
          Dev-Runtime (Hot-Reload aus dem philippos-Checkout); admin/user sehen die Wartungsseite.
          Im <strong>Stabil-Modus</strong> läuft der freigegebene, gebackene Stand.
        </p>
        <p style={{ fontSize: 13, margin: "8px 0" }}>
          Aktueller Modus:{" "}
          <strong style={{ color: mode === "live" ? "#c2410c" : "#15803d" }}>
            {mode === null ? "…" : mode === "live" ? "LIVE (Dev-Runtime)" : "STABIL (Kundenbetrieb)"}
          </strong>
          {transition && <span className="muted"> — Übergang läuft ({transition})…</span>}
          {mode === "live" && git.branch ? (
            <span className="muted" style={{ marginLeft: 10 }}>
              Branch: <code>{git.branch}</code>{git.commit && <> @ <code>{git.commit}</code></>}
            </span>
          ) : version ? (
            <span className="muted" style={{ marginLeft: 10 }}>Version: {version}</span>
          ) : null}
        </p>
        <div style={{ display: "flex", gap: 12, marginTop: 10 }}>
          <button className="primary" onClick={toLive} disabled={busy || mode === "live" || !!transition}>
            Live-Modus aktivieren
          </button>
          <button onClick={toStable} disabled={busy || mode === "stable" || !!transition}>
            Zurück auf Stabil
          </button>
        </div>
        {msg && <p style={{ fontSize: 13, marginTop: 10 }}>{msg}</p>}
      </div>
    </div>
  );
}
